import {onBeforeUnmount, ref} from '#imports';
import {readonlyState} from '@malt/utils';

export enum PollingResult {
    SUCCESS,
    FAILED,
}

export function usePolling() {
    let timeout: ReturnType<typeof setTimeout> | null = null;
    let retryCount: number = 0;
    const isPolling = ref<boolean>(false);

    interface PollingOptions {
        delayInMs: number;
        maxRetries: number;
    }

    async function startPolling(callback: () => Promise<PollingResult>, onError: () => void, options: PollingOptions) {
        const {delayInMs, maxRetries} = options;

        const poll = async () => {
            try {
                const result = await callback();
                if (result === PollingResult.SUCCESS) {
                    stopPolling();
                    return;
                }
            } catch (error) {
                console.warn('Polling error: ', error);
            }

            if (retryCount > maxRetries) {
                onError();
                stopPolling();
                return;
            }

            retryCount++;
            timeout = setTimeout(poll, delayInMs);
        };

        isPolling.value = true;
        await poll();
    }

    function stopPolling() {
        if (timeout) {
            clearTimeout(timeout);
        }
        isPolling.value = false;
        retryCount = 0;
    }

    onBeforeUnmount(() => {
        if (isPolling.value) {
            stopPolling();
        }
    });

    return {
        isPolling: readonlyState(isPolling),
        startPolling,
        stopPolling,
    };
}
